import { useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { shortName } from "../../auth/scope";
import {
  LICENSE_TYPES,
  createSubOperator,
  isValidIndianMobile,
  isValidLabel,
  subOperatorKindsFor,
  type SubOperatorKind,
} from "../../api/suboperators";
import { useProviderKind } from "./useProviderKind";

/**
 * Providers › + Add New Provider (owner, 2026-09-19): the operator adds a
 * provider beneath the namespace it is acting on, says what that provider IS
 * to it, and gives it a name and a contact.
 *
 * ⛔ No bank, PAN, GSTIN or KYC here: radaccess writes the profile, licence
 * shape, contact and relationship only. Those stay with the platform.
 */
export function AddProviderScreen() {
  const identity = useIdentity();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const parent = identity.namespace ?? "";

  const kinds = subOperatorKindsFor(useProviderKind());
  const [chosen, setKind] = useState<SubOperatorKind>("pin");
  // The type loads after the first render: a kind this parent may not add
  // falls back to the first one it may.
  const kind = kinds.some((k) => k.kind === chosen) ? chosen : (kinds[0]?.kind ?? "site");
  const [label, setLabel] = useState("");
  const [legalname, setLegalname] = useState("");
  const [operatortype, setOperatortype] = useState("OPERATOR");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [licensetype, setLicensetype] = useState("");
  const [licensenumber, setLicensenumber] = useState("");

  const labelOk = isValidLabel(label);
  const mobileOk = isValidIndianMobile(mobile);
  const licenceOk = kind !== "licensed" || (licensetype !== "" && licensenumber.trim() !== "");
  const ready = Boolean(parent) && labelOk && legalname.trim() !== "" && mobileOk && licenceOk;

  const save = useMutation({
    mutationFn: () =>
      createSubOperator(identity, parent, { label, kind, legalname, operatortype, mobile, email, licensetype, licensenumber }),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["providers"] }),
        qc.invalidateQueries({ queryKey: ["suboperators", parent] }),
      ]);
      navigate("/providers");
    },
  });

  const child = label.trim() ? `${parent}.${label.trim().toLowerCase()}` : `${parent}.…`;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 720 }}>
      <Box>
        <Typography variant="h5" component="h1">
          Add new provider
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Beneath {parent ? shortName(parent) : "your namespace"} · <code>{child}</code>
        </Typography>
      </Box>

      <Paper sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
        <FormControl>
          <FormLabel id="kind-label">What is this provider to you?</FormLabel>
          <RadioGroup aria-labelledby="kind-label" value={kind} onChange={(e) => setKind(e.target.value as SubOperatorKind)}>
            {kinds.map((k) => (
              <FormControlLabel
                key={k.kind}
                value={k.kind}
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {k.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {k.help}
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: "flex-start", mb: 1, "& .MuiRadio-root": { pt: 0.5 } }}
              />
            ))}
          </RadioGroup>
        </FormControl>

        <TextField
          label="Short name (becomes the namespace)"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          required
          error={label !== "" && !labelOk}
          helperText={label !== "" && !labelOk ? "Lowercase letters, digits and hyphens; up to 32; not starting or ending with a hyphen." : "e.g. ganesh — cannot be changed later"}
        />
        <TextField label="Legal name" value={legalname} onChange={(e) => setLegalname(e.target.value)} required />
        {kind === "pin" || kind === "franchise" ? (
          // radaccess sets PIN / FRANCHISE from the kind and ignores a sent type.
          <TextField label="Provider type" value={kind === "pin" ? "PIN operator" : "Franchise"} disabled helperText="Set by the kind" />
        ) : (
          <TextField select label="Provider type" value={operatortype} onChange={(e) => setOperatortype(e.target.value)}>
            <MenuItem value="OPERATOR">Operator</MenuItem>
            <MenuItem value="VNO">VNO</MenuItem>
            <MenuItem value="ISP">ISP</MenuItem>
          </TextField>
        )}
        {kind === "licensed" && (
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <TextField select label="Licence type" value={licensetype} onChange={(e) => setLicensetype(e.target.value)} required sx={{ minWidth: 180 }}>
              {LICENSE_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {t.replace("_", " ")}
                </MenuItem>
              ))}
            </TextField>
            <TextField label="Licence number" value={licensenumber} onChange={(e) => setLicensenumber(e.target.value)} required sx={{ flexGrow: 1 }} />
          </Box>
        )}
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <TextField
            label="Mobile"
            value={mobile}
            onChange={(e) => setMobile(e.target.value)}
            required
            error={mobile !== "" && !mobileOk}
            helperText={mobile !== "" && !mobileOk ? "A 10-digit Indian mobile number." : " "}
            sx={{ minWidth: 220 }}
          />
          <TextField label="Email (optional)" type="email" value={email} onChange={(e) => setEmail(e.target.value)} helperText=" " sx={{ flexGrow: 1 }} />
        </Box>
        {kind === "pin" && (
          <Alert severity="info">
            An unlicensed provider works only on PINs bought from you. PIN plans and PIN funds come next; until then this provider is set up but has no PINs.
          </Alert>
        )}
        {save.error && <Alert severity="error">{save.error instanceof Error ? save.error.message : String(save.error)}</Alert>}
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button component={RouterLink} to="/providers">
            Cancel
          </Button>
          <Button variant="contained" disabled={!ready || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? "Adding…" : "Add provider"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
